// API utility functions

export const checkAuth = async () => {
  try {
    const res = await fetch('/api/whoami');
    const data = await res.json();
    return data;
  } catch (err) {
    console.error('Auth check failed:', err);
    return { login: null, error: err.message };
  }
};

export const logoutUser = async () => {
  try {
    await fetch('/api/logout');
    return { success: true };
  } catch (err) {
    console.error('Logout failed:', err);
    return { success: false, error: err.message };
  }
};

export const fetchBillingUsage = async () => {
  try {
    const res = await fetch('/api/billing/usage');
    const data = await res.json();
    if (!res.ok) {
      return { success: false, error: data.error || 'Failed to load billing usage' };
    }
    return { success: true, data };
  } catch (err) {
    console.error('Billing usage fetch failed:', err);
    return { success: false, error: err.message };
  }
};

export const createCheckoutSession = async (frontendOrigin) => {
  try {
    const res = await fetch('/api/billing/create-checkout-session', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ frontend_origin: frontendOrigin }),
    });
    const data = await res.json();
    if (!res.ok) {
      return { success: false, error: data.error || 'Failed to start checkout' };
    }
    return { success: true, data };
  } catch (err) {
    console.error('Checkout session creation failed:', err);
    return { success: false, error: err.message };
  }
};

export const fetchUserFiles = async () => {
  try {
    const timestamp = new Date().getTime();
    const res = await fetch(`/api/files?_t=${timestamp}`, {
      cache: 'no-cache',
      headers: {
        'Cache-Control': 'no-cache',
        'Pragma': 'no-cache'
      }
    });
    const data = await res.json();
    return data && Array.isArray(data.files) ? data.files : [];
  } catch (err) {
    console.error('Error fetching files:', err);
    return [];
  }
};

export const uploadFile = async (formData) => {
  try {
    const res = await fetch('/api/files/upload', { 
      method: 'POST', 
      body: formData 
    });
    const data = await res.json();
    if (!res.ok) {
      return { success: false, error: data.error || 'Upload failed', data };
    }
    return { success: true, data };
  } catch (err) {
    console.error('Upload failed:', err);
    return { success: false, error: err.message };
  }
};

export const deleteFile = async (fileId) => {
  try {
    const res = await fetch(`/api/files/${fileId}`, { method: 'DELETE' });
    if (!res.ok) throw new Error('Server returned an error');
    return { success: true };
  } catch (err) {
    console.error('Delete failed:', err);
    return { success: false, error: err.message };
  }
};

export const fetchPiiSuggestions = async (fileId) => {
  try {
    const res = await fetch('/api/pii/suggestions', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ file_id: fileId }),
    });
    const data = await res.json();
    if (!res.ok) {
      return { success: false, error: data.error || 'Failed to load suggestions' };
    }
    return {
      success: true,
      suggestions: data.suggestions || {},
      total: data.total || 0,
      scanInfo: data.scan_info || null,
    };
  } catch (err) {
    console.error('PII suggestions fetch failed:', err);
    return { success: false, error: err.message };
  }
};


export const saveRedactions = async (fileId, redactions) => {
  try {
    const response = await fetch('/api/redact', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        file_id: fileId,
        redactions: redactions
      })
    });

    let data;
    try {
      data = await response.json();
    } catch {
      const text = await response.text();
      const snippet = text ? text.slice(0, 200) : '';
      return {
        success: false,
        error: `HTTP ${response.status} non-JSON response: ${snippet}`,
      };
    }
    if (!response.ok) {
      return {
        success: false,
        error: data.message || data.error || 'Failed to save redactions',
        upgradeRequired: Boolean(data.upgrade_required),
        loginRequired: Boolean(data.login_required),
        usage: data.usage || null,
      };
    }
    return data;
  } catch (err) {
    console.error('Error saving redactions:', err);
    return { success: false, error: err.message };
  }
};
export const handleSavePattern = async (patternName, stateData, currentPage) => {
  console.log('Saving pattern:', stateData.pdfPath);
  if (!patternName) {
    return { success: false, error: 'You must set a pattern name.' };
  }
  if (!stateData?.pdfPath) {
    return { success: false, error: 'No PDF loaded.' };
  }

  try {
    const res = await fetch(`/api/patterns/save/${patternName}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        im: stateData.pdfPath.replace('.pdf', '') + '-' + currentPage,
        matches: stateData.redactions?.[currentPage] || [],
        wipes: [],
        user_id: stateData.userId || null,
      }),
      credentials: 'include',
    });

    const data = await res.json();
    if (res.ok) {
      return { success: true, data };
    }
    return { success: false, error: data.error || 'Failed to save pattern', data };
  } catch (err) {
    return { success: false, error: err.message };
  }
};

export const applyPattern = async (patternName, stateData, currentPage) => {
  if (!stateData?.pdfPath) {
    return { success: false, error: 'No PDF loaded.' };
  }
  try {
    const res = await fetch(`/api/patterns/${patternName}/redact`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        pdfPath: stateData.pdfPath.replace('.pdf', '') + '-' + currentPage,
        matches: [],
        wipes:  [],
        user_id: stateData.userId,
        file_id:stateData.currentPdfId    })
    });
    let data;
    try {
      data = await res.json();
    } catch {
      const text = await res.text();
      const snippet = text ? text.slice(0, 200) : '';
      throw new Error(`HTTP ${res.status} non‑JSON response: ${snippet}`);
    }
    if (!res.ok) {
      throw new Error(data.error || `HTTP ${res.status} applying pattern`);
    }
    console.log('Pattern applied:', stateData);
    return { success: true, data };
  } catch (err) {
    console.error('Error applying pattern:', err);
    return { success: false, error: err.message };
  }
};

// Patterns API functions
export const fetchPatterns = async () => {
  try {
    const res = await fetch('/api/patterns');
    const data = await res.json();
    return Array.isArray(data) ? data : [];
  } catch (err) {
    console.error('Error fetching patterns:', err);
    return [];
  }
};

export const deletePattern = async (patternName) => {
  try {
    const res = await fetch(`/api/patterns/delete/${patternName}`, { method: 'DELETE' });
    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      return { success: false, error: data.error || 'Failed to delete pattern' };
    }
    return { success: true };
  } catch (err) {
    return { success: false, error: err.message };
  }
};

export const renamePattern = async (patternName, newName) => {
  try {
    const res = await fetch(`/api/patterns/rename/${patternName}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ new_name: newName }),
    });
    const data = await res.json();
    if (!res.ok) {
      return { success: false, error: data.error || 'Failed to rename pattern' };
    }
    return { success: true, name: data.name };
  } catch (err) {
    return { success: false, error: err.message };
  }
};

export const getPattern = async (patternName) => {
  try {
    const res = await fetch(`/api/patterns/${patternName}`);
    if (!res.ok) throw new Error('Pattern not found');
    return res.blob(); 
  } catch (err) {
    console.error('Error fetching pattern:', err);
    return null;
  }
};

export const submitContactForm = async (payload) => {
  try {
    const res = await fetch('/api/contact', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    const data = await res.json();
    if (!res.ok) {
      return { success: false, error: data.error || 'Contact request failed.' };
    }
    return { success: true, data };
  } catch (err) {
    console.error('Contact form submission failed:', err);
    return { success: false, error: err.message };
  }
};
